/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./config.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query';
import { chunk } from 'es-toolkit';
import { SIGNUP_API_ENDPOINTS as cfg } from './config';
import type { EnableMfaResponse } from './api.generated';

type Definitions = DefinitionsFromApi<typeof generatedApi>;
type UpdatedDefinitions = Omit<
  Definitions,
  typeof cfg.verifyMfa.generated | typeof cfg.generateMfaQr.generated
> & {
  [cfg.verifyMfa.generated]: OverrideResultType<
    Definitions[typeof cfg.verifyMfa.generated],
    EnableMfaResponse & { codesColumns: string[][] }
  >;
  [cfg.generateMfaQr.generated]: OverrideResultType<
    Definitions[typeof cfg.generateMfaQr.generated],
    { success: boolean; data: string }
  >;
};

/** Cache tag of the password reset session (`GET /api/v1/password/session`). */
export const PASSWORD_RESET_SESSION_TAG = 'PasswordResetSession';

/**
 * Refetch the reset session only after a step succeeded. A failed attempt does
 * not move the session forward, and the UI resyncs explicitly on the errors that
 * mean it did change (expired, locked out, out of order); every `/session` call
 * counts against auth-api's per-IP rate limit.
 */
const invalidateSessionOnSuccess = (
  _result: unknown,
  error: unknown,
): (typeof PASSWORD_RESET_SESSION_TAG)[] => (error ? [] : [PASSWORD_RESET_SESSION_TAG]);

// Enhance the generated API with response transformation logic and the
// password reset session tag.
//
// The password reset session is the only query of the Signup API. Every other
// endpoint is a mutation; the access token lives in its own slice and is updated
// by `setupSignupListeners` (see `./listeners.ts`), not through the RTK Query cache.
const enhancedSignupApi = generatedApi.enhanceEndpoints<
  typeof PASSWORD_RESET_SESSION_TAG,
  UpdatedDefinitions
>({
  addTagTypes: [PASSWORD_RESET_SESSION_TAG],
  endpoints: {
    [cfg.verifyMfa.generated]: {
      // Format recovery codes and compute columns for display
      transformResponse: (
        response: EnableMfaResponse,
      ): EnableMfaResponse & { codesColumns: string[][] } => {
        const recoveryCodes = response.recoveryCodes.map((code) =>
          code.replace(/(.{4})/, '$1\u2013'),
        );
        // Create 8 columns with 1 code each
        const codesColumns = chunk(recoveryCodes, 1).slice(0, 8);
        return { ...response, recoveryCodes, codesColumns };
      },
    },
    [cfg.getPasswordResetSession.generated]: {
      providesTags: [PASSWORD_RESET_SESSION_TAG],
    },
    [cfg.requestPasswordReset.generated]: {
      invalidatesTags: invalidateSessionOnSuccess,
    },
    [cfg.verifyPasswordResetEmail.generated]: {
      invalidatesTags: invalidateSessionOnSuccess,
    },
    [cfg.verifyPasswordReset2fa.generated]: {
      invalidatesTags: invalidateSessionOnSuccess,
    },
    [cfg.verifyPasswordResetRecoveryCode.generated]: {
      invalidatesTags: invalidateSessionOnSuccess,
    },
  },
});

export const signupApi: typeof enhancedSignupApi = enhancedSignupApi;

// Re-export hooks with cleaner names via endpoint definitions
export const hookExports: HookExports<typeof cfg, typeof signupApi> = createHookExports(
  cfg,
  signupApi,
);

// Re-export endpoints object with cleaner names for direct usage
const endpointExports = createEndpointExports(cfg, signupApi);

export const signupApiEndpoints: typeof endpointExports = endpointExports;
